import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/supabase/client';
import React from 'react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Heading2, Muted, Small } from '@/components/Typography';
import { ImageWithFallback } from '@/components/ImageWithFallback';
import { authMessages } from './auth.messages';
import { ROUTES } from '@/routes';

type AuthMode = 'login' | 'signup' | 'forgotPassword';

export function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const [success, setSuccess] = useState<string | null>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(null);
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    setLoading(false);
    if (error) {
      toast.error(error.message);
    } else {
      navigate(ROUTES.HOME);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(null);

    if (password !== confirmPassword) {
      toast.error(authMessages.signup.passwordMismatch);
      setLoading(false);
      return;
    }

    const { error } = await supabase.auth.signUp({ email, password });
    setLoading(false);
    if (error) {
      toast.error(error.message);
    } else {
      setSuccess(authMessages.signup.emailConfirmation);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(null);

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}${ROUTES.LOGIN}`,
    });

    setLoading(false);
    if (error) {
      toast.error(error.message);
    } else {
      setSuccess(authMessages.forgotPassword.resetEmailSent);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    switch (mode) {
      case 'login':
        return handleLogin(e);
      case 'signup':
        return handleSignup(e);
      case 'forgotPassword':
        return handleForgotPassword(e);
    }
  };

  const switchMode = (newMode: AuthMode) => {
    setMode(newMode);
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setSuccess(null);
  };

  const getMessages = () => {
    switch (mode) {
      case 'login':
        return authMessages.login;
      case 'signup':
        return authMessages.signup;
      case 'forgotPassword':
        return authMessages.forgotPassword;
    }
  };

  const messages = getMessages();

  return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <div className="mx-4 grid w-full max-w-4xl grid-cols-1 gap-0 overflow-hidden rounded-2xl shadow-2xl lg:grid-cols-2">
        {/* Main card with form */}
        <Card className="rounded-none border-0 p-8 shadow-none">
          <div className="space-y-6">
            <div className="text-center">
              <Heading2 className="text-stone-900">{messages.title}</Heading2>
              <Muted>{messages.subtitle}</Muted>
              {success && (
                <div className="mt-4 rounded-md border border-green-200 bg-green-50 p-3">
                  <Small className="text-green-800">{success}</Small>
                </div>
              )}
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <Label htmlFor="email">{messages.emailLabel}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={messages.emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              {mode !== 'forgotPassword' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">
                      {mode === 'login'
                        ? authMessages.login.passwordLabel
                        : authMessages.signup.passwordLabel}
                    </Label>
                    {mode === 'login' && (
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        onClick={() => switchMode('forgotPassword')}
                      >
                        {authMessages.login.forgotPassword}
                      </Button>
                    )}
                  </div>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              )}

              {mode === 'signup' && (
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">
                    {authMessages.signup.confirmPasswordLabel}
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
              )}

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? messages.loadingButton : messages.submitButton}
              </Button>
            </form>

            <div className="text-center text-sm text-stone-600">
              {mode === 'login' && (
                <>
                  {authMessages.login.noAccount}
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={() => switchMode('signup')}
                  >
                    {authMessages.login.signUpLink}
                  </Button>
                </>
              )}
              {mode === 'signup' && (
                <>
                  {authMessages.signup.hasAccount}
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={() => switchMode('login')}
                  >
                    {authMessages.signup.signInLink}
                  </Button>
                </>
              )}
              {mode === 'forgotPassword' && (
                <>
                  {authMessages.forgotPassword.rememberedPassword}
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={() => switchMode('login')}
                  >
                    {authMessages.forgotPassword.loginLink}
                  </Button>
                </>
              )}
            </div>
          </div>
        </Card>

        {/* Image section */}
        <div className="hidden h-[500px] overflow-hidden rounded-r-2xl lg:block">
          <ImageWithFallback
            src="https://images.unsplash.com/photo-1644416598043-11c2816eec28?q=80&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            alt={authMessages.backgroundImageAlt}
            className="h-full w-full object-cover"
          />
        </div>
      </div>
    </div>
  );
}
